import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  ArrowRight,
  ArrowLeft,
  Eye,
  EyeOff,
  Check,
  Home,
  Search,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getAvatarColor, getUserInitials } from '@/components/common/avatarHelpers';
import { cn } from '@/lib/utils';
import { useCreateUser, useProperties } from '@/hooks/useUsers';

interface NewUserWizardDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

type Cargo = 'Proprietário' | 'Funcionário';

const CARGO_CARDS: { value: Cargo; title: string; desc: string }[] = [
  { value: 'Proprietário', title: 'Proprietário', desc: 'acesso total, tem propriedade' },
  { value: 'Funcionário', title: 'Funcionário', desc: 'vincula a uma propriedade existente' },
];

const BR_UFS = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
];

const inputClass =
  'w-full rounded-lg border border-border-strong bg-muted px-3 py-2 text-sm text-text-title placeholder:text-text-hint transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30';
const labelClass = 'mb-1.5 block text-sm font-medium text-text-title';

const EMPTY_AREAS = { pastagem_ha: '', reserva_legal: '', sistema_ilpf: '', area_agricola: '' };

export function NewUserWizardDrawer({ isOpen, onClose }: NewUserWizardDrawerProps) {
  const createMutation = useCreateUser();
  const isSaving = createMutation.isPending;

  const [step, setStep] = useState<1 | 2>(1);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [cargo, setCargo] = useState<Cargo>('Proprietário');
  // Proprietário
  const [propName, setPropName] = useState('');
  const [city, setCity] = useState('');
  const [uf, setUf] = useState('');
  const [areas, setAreas] = useState<typeof EMPTY_AREAS>(EMPTY_AREAS);
  // Funcionário
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(null);
  const [propSearch, setPropSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: properties, isLoading: propsLoading } = useProperties(
    undefined,
    isOpen && cargo === 'Funcionário'
  );

  const reset = () => {
    setStep(1);
    setName('');
    setPhone('');
    setPassword('');
    setShowPassword(false);
    setCargo('Proprietário');
    setPropName('');
    setCity('');
    setUf('');
    setAreas(EMPTY_AREAS);
    setSelectedPropertyId(null);
    setPropSearch('');
    setError(null);
  };

  const handleClose = () => {
    if (isSaving) return;
    reset();
    onClose();
  };

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', onEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', onEsc);
      document.body.style.overflow = 'unset';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isSaving]);

  const areasTotal = useMemo(
    () =>
      (parseFloat(areas.pastagem_ha) || 0) +
      (parseFloat(areas.reserva_legal) || 0) +
      (parseFloat(areas.sistema_ilpf) || 0) +
      (parseFloat(areas.area_agricola) || 0),
    [areas]
  );

  const phoneDigits = phone.replace(/\D/g, '').length;
  const step1Valid = name.trim().length >= 3 && phoneDigits >= 10 && password.length >= 6;

  const step2Valid =
    cargo === 'Funcionário'
      ? selectedPropertyId != null
      : propName.trim().length >= 3 && city.trim().length >= 2 && uf.length === 2;

  const filteredProps = useMemo(() => {
    const q = propSearch.trim().toLowerCase();
    const list = properties ?? [];
    if (!q) return list;
    return list.filter((p) => p.name.toLowerCase().includes(q) || p.city.toLowerCase().includes(q));
  }, [properties, propSearch]);

  const handleSubmit = async () => {
    if (!step2Valid) return;
    setError(null);
    const base = { name: name.trim(), phone: phone.replace(/[^\d+]/g, ''), password };
    const payload =
      cargo === 'Funcionário'
        ? { ...base, cargo, propriedadeId: selectedPropertyId as number }
        : {
            ...base,
            cargo,
            property: {
              name: propName.trim(),
              city: city.trim(),
              uf,
              area_total: areasTotal,
              pastagem_ha: parseFloat(areas.pastagem_ha) || 0,
              reserva_legal: parseFloat(areas.reserva_legal) || 0,
              sistema_ilpf: parseFloat(areas.sistema_ilpf) || 0,
              area_agricola: parseFloat(areas.area_agricola) || 0,
            },
          };
    try {
      await createMutation.mutateAsync(payload);
      handleClose();
    } catch (err) {
      const fallback = 'Erro ao criar usuário. Verifique os dados e tente novamente.';
      let msg = fallback;
      if (typeof err === 'object' && err !== null) {
        const maybe = err as { response?: { data?: { message?: string; error?: string } } };
        msg = maybe.response?.data?.message ?? maybe.response?.data?.error ?? fallback;
      }
      setError(msg);
    }
  };

  if (!isOpen) return null;

  const isFuncionario = cargo === 'Funcionário';

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end"
      role="dialog"
      aria-modal="true"
      aria-label="Novo Usuário"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm dark:bg-black/70"
        onClick={handleClose}
      />

      <div className="relative z-10 flex h-full w-full max-w-md animate-slide-in-right flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] border-l border-border bg-bg-elevated shadow-2xl ring-1 ring-black/5 dark:ring-white/10">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-xl font-semibold text-text-title">Novo Usuário</h2>
          <button
            onClick={handleClose}
            aria-label="Fechar"
            className="rounded-lg p-1 text-text-secondary transition-colors hover:bg-muted hover:text-text-title"
          >
            <X size={20} />
          </button>
        </div>

        {/* Stepper */}
        <div className="border-b border-border px-6 py-4">
          <div className="flex items-center gap-3">
            <StepPill num={1} label="Usuário" state={step === 1 ? 'active' : 'done'} />
            <div
              className={cn('h-0.5 flex-1 rounded-full', step === 2 ? 'bg-success' : 'bg-border')}
            />
            <StepPill num={2} label="Propriedade" state={step === 2 ? 'active' : 'todo'} />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-error/30 bg-error/10 px-4 py-3 text-sm text-error">
              <AlertCircle size={18} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {step === 1 ? (
            <div className="space-y-5">
              <div>
                <label className={labelClass}>Nome completo *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={inputClass}
                  placeholder="Digite o nome completo"
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Telefone *</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className={inputClass}
                    placeholder="+55 (67) 9..."
                  />
                </div>
                <div>
                  <label className={labelClass}>Senha *</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={cn(inputClass, 'pr-10')}
                      placeholder="Mínimo 6 caracteres"
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-title"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className={labelClass}>Cargo *</label>
                <div className="space-y-2" role="radiogroup" aria-label="Cargo">
                  {CARGO_CARDS.map((c) => {
                    const active = cargo === c.value;
                    return (
                      <button
                        key={c.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => setCargo(c.value)}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors',
                          active
                            ? 'border-primary bg-primary/10'
                            : 'border-border-strong bg-muted hover:border-primary/50'
                        )}
                      >
                        <span
                          className={cn(
                            'flex size-4 shrink-0 items-center justify-center rounded-full border-2',
                            active ? 'border-primary' : 'border-text-hint'
                          )}
                        >
                          {active && <span className="size-2 rounded-full bg-primary" />}
                        </span>
                        <span className="text-sm">
                          <span className="font-semibold text-text-title">{c.title}</span>
                          <span className="text-text-secondary"> · {c.desc}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs text-text-secondary">
                  {isFuncionario
                    ? 'Na etapa 2 você vincula o funcionário a uma propriedade existente.'
                    : 'Na etapa 2 você cria a propriedade deste proprietário.'}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Resumo do usuário criado */}
              <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/40 px-4 py-3">
                <div className="flex min-w-0 items-center gap-3">
                  <div
                    className={cn(
                      'flex size-9 shrink-0 items-center justify-center rounded-full text-sm font-bold',
                      getAvatarColor(name || 'x')
                    )}
                  >
                    {getUserInitials(name || '?')}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-text-title">
                      {name || 'Sem nome'} · {cargo}
                    </p>
                    <p className="truncate text-xs text-text-secondary">{phone || '—'}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="shrink-0 text-sm font-medium text-primary hover:underline"
                >
                  Editar
                </button>
              </div>

              {isFuncionario ? (
                /* Seletor de propriedade existente */
                <div>
                  <label className={labelClass}>Propriedade *</label>
                  <div className="relative mb-2">
                    <Search
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
                    />
                    <input
                      type="text"
                      value={propSearch}
                      onChange={(e) => setPropSearch(e.target.value)}
                      className={cn(inputClass, 'pl-9')}
                      placeholder="Buscar por nome ou cidade"
                    />
                  </div>
                  <div className="max-h-72 space-y-1.5 overflow-y-auto">
                    {propsLoading ? (
                      <div className="flex items-center gap-2 py-4 text-sm text-text-secondary">
                        <RefreshCw size={16} className="animate-spin" /> Carregando propriedades…
                      </div>
                    ) : filteredProps.length === 0 ? (
                      <p className="py-4 text-sm text-text-secondary">
                        Nenhuma propriedade encontrada.
                      </p>
                    ) : (
                      filteredProps.map((p) => {
                        const active = selectedPropertyId === p.id;
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => setSelectedPropertyId(p.id)}
                            className={cn(
                              'flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
                              active
                                ? 'border-primary bg-primary/10'
                                : 'border-border-strong bg-muted hover:border-primary/50'
                            )}
                          >
                            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-tertiary/10 text-tertiary">
                              <Home size={15} />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium text-text-title">
                                {p.name}
                              </span>
                              <span className="block truncate text-xs text-text-secondary">
                                {p.city}
                                {p.uf ? `, ${p.uf}` : ''}
                                {p.ownerName ? ` · ${p.ownerName}` : ''}
                              </span>
                            </span>
                            {active && <Check size={16} className="shrink-0 text-primary" />}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              ) : (
                /* Criar propriedade nova */
                <>
                  <div>
                    <label className={labelClass}>Nome da propriedade *</label>
                    <input
                      type="text"
                      value={propName}
                      onChange={(e) => setPropName(e.target.value)}
                      className={inputClass}
                      placeholder="Ex: Fazenda Santa Maria"
                      autoFocus
                    />
                  </div>

                  <div className="grid grid-cols-[1fr_88px] gap-4">
                    <div>
                      <label className={labelClass}>Cidade *</label>
                      <input
                        type="text"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        className={inputClass}
                        placeholder="Ex: Cuiabá"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>UF *</label>
                      <Select value={uf} onValueChange={setUf}>
                        <SelectTrigger className="h-[38px] w-full rounded-lg border border-border-strong bg-muted shadow-none">
                          <SelectValue placeholder="UF" />
                        </SelectTrigger>
                        <SelectContent>
                          {BR_UFS.map((u) => (
                            <SelectItem key={u} value={u}>
                              {u}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Áreas */}
                  <div className="rounded-lg border border-border p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <p className="text-sm font-semibold text-text-title">
                        Áreas (ha) <span className="font-normal text-text-secondary">opcional</span>
                      </p>
                      <p className="text-sm text-text-secondary">
                        total <span className="font-semibold text-text-title">{areasTotal}</span>
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <AreaInput
                        label="Pastagem"
                        value={areas.pastagem_ha}
                        onChange={(v) => setAreas({ ...areas, pastagem_ha: v })}
                      />
                      <AreaInput
                        label="Reserva legal"
                        value={areas.reserva_legal}
                        onChange={(v) => setAreas({ ...areas, reserva_legal: v })}
                      />
                      <AreaInput
                        label="Sistema ILPF"
                        value={areas.sistema_ilpf}
                        onChange={(v) => setAreas({ ...areas, sistema_ilpf: v })}
                      />
                      <AreaInput
                        label="Área agrícola"
                        value={areas.area_agricola}
                        onChange={(v) => setAreas({ ...areas, area_agricola: v })}
                      />
                    </div>
                    <p className="mt-3 text-xs text-text-secondary">
                      Área total calculada automaticamente pela soma.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-border bg-bg-elevated px-6 py-4">
          {step === 2 ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setStep(1)}
              disabled={isSaving}
            >
              <ArrowLeft size={16} />
              Voltar
            </Button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-3">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isSaving}>
              Cancelar
            </Button>
            {step === 1 ? (
              <Button type="button" onClick={() => setStep(2)} disabled={!step1Valid}>
                Próximo: Propriedade
                <ArrowRight size={16} />
              </Button>
            ) : (
              <Button type="button" onClick={handleSubmit} disabled={!step2Valid || isSaving}>
                {isSaving && <RefreshCw size={16} className="animate-spin" />}
                {isSaving
                  ? 'Criando...'
                  : isFuncionario
                    ? 'Criar usuário e vincular'
                    : 'Criar usuário e propriedade'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function StepPill({
  num,
  label,
  state,
}: {
  num: number;
  label: string;
  state: 'active' | 'done' | 'todo';
}) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          'flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-bold',
          state === 'active' && 'bg-primary text-white',
          state === 'done' && 'bg-success text-white',
          state === 'todo' && 'bg-muted text-text-secondary'
        )}
      >
        {state === 'done' ? <Check size={14} /> : num}
      </span>
      <span
        className={cn(
          'text-sm font-medium',
          state === 'todo' ? 'text-text-secondary' : 'text-text-title'
        )}
      >
        {label}
      </span>
    </div>
  );
}

function AreaInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-text-secondary">{label}</label>
      <input
        type="number"
        min="0"
        step="0.01"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
        placeholder="0"
      />
    </div>
  );
}
